import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgentDto, HandoffRuleRequestDto } from '@shared/services/apiService/apiService';
import {
  buildHandoffCanvasGraph,
  createEmptyHandoffRule,
  parseHandoffRulesValue,
  pruneHandoffCanvasToReferencedAgents,
  serializeHandoffRules,
  serializeHandoffRulesDocument,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';
import { HandoffRuleEditorValue } from '@features/Admin/Agent/handoffRules/domain/contracts';
import {
  HandoffRuleIssueCode,
  validateHandoffRules,
} from '@features/Admin/Agent/handoffRules/domain/validation';

type FocusedSection = 'route' | 'destinationContext' | 'variableExtraction' | 'toolMessages';
type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const ASSISTANT_SPAWN_X_START = 120;
const ASSISTANT_SPAWN_Y_START = 120;
const ASSISTANT_SPAWN_X_STEP = 300;
const ASSISTANT_SPAWN_Y_STEP = 160;
const ASSISTANT_SPAWN_MAX_COLUMNS = 3;

type Params = {
  value: string;
  onChange: (value: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onValidationChange?: (hasErrors: boolean) => void;
  invalidJsonMessage: string;
  issueMessages: Record<HandoffRuleIssueCode, string>;
};

const getNextAssistantSpawnPosition = (
  nodePositions: Record<string, { x: number; y: number }>,
): { x: number; y: number } => {
  const occupiedSlots = new Set(Object.values(nodePositions).map(({ x, y }) => `${x}:${y}`));

  let row = 0;
  while (true) {
    for (let column = 0; column < ASSISTANT_SPAWN_MAX_COLUMNS; column += 1) {
      const position = {
        x: ASSISTANT_SPAWN_X_START + column * ASSISTANT_SPAWN_X_STEP,
        y: ASSISTANT_SPAWN_Y_START + row * ASSISTANT_SPAWN_Y_STEP,
      };
      if (!occupiedSlots.has(`${position.x}:${position.y}`)) return position;
    }
    row += 1;
  }
};

export const useHandoffRulesEditorState = ({
  value,
  onChange,
  onDirtyChange,
  onValidationChange,
  invalidJsonMessage,
  issueMessages,
}: Params) => {
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'structured' | 'json'>('structured');
  const [rules, setRules] = useState<HandoffRuleEditorValue[]>([]);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [canvasAgentIds, setCanvasAgentIds] = useState<string[]>([]);
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>({});
  const [isAssistantPickerOpen, setIsAssistantPickerOpen] = useState(false);
  const [assistantSearch, setAssistantSearch] = useState('');
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [focusedSection, setFocusedSection] = useState<FocusedSection>('route');
  const migratedInputRef = useRef<string | null>(null);
  const lastLocalSerializedValueRef = useRef<string | null>(null);
  const lastSavedSerializedValueRef = useRef<string>('[]');
  const lastValidSerializedValueRef = useRef<string>('[]');
  const canvasAgentIdsRef = useRef<string[]>([]);
  const nodePositionsRef = useRef<Record<string, { x: number; y: number }>>({});

  useEffect(() => {
    canvasAgentIdsRef.current = canvasAgentIds;
  }, [canvasAgentIds]);

  useEffect(() => {
    nodePositionsRef.current = nodePositions;
  }, [nodePositions]);

  const toSerializedDocumentValue = useCallback(
    (
      nextRules: HandoffRuleEditorValue[],
      nextCanvasAgentIds: string[],
      nextNodePositions: Record<string, { x: number; y: number }>,
    ) =>
      JSON.stringify(
        serializeHandoffRulesDocument(nextRules, nextCanvasAgentIds, nextNodePositions),
        null,
        2,
      ),
    [],
  );
  const serializedRulesValue = useMemo(
    () => toSerializedDocumentValue(rules, canvasAgentIds, nodePositions),
    [canvasAgentIds, nodePositions, rules, toSerializedDocumentValue],
  );
  const isDirty = serializedRulesValue !== lastSavedSerializedValueRef.current;

  const buildRuleFingerprint = useCallback((rule: HandoffRuleEditorValue) => {
    return JSON.stringify({
      ruleType: rule.ruleType,
      name: rule.name,
      description: rule.description,
      fromAgentId: rule.fromAgentId,
      toAgentId: rule.toAgentId,
      messages: rule.messages,
      contextPropagationPlan: rule.contextPropagationPlan,
      contextSummaryPlan: rule.contextSummaryPlan,
      sessionBoundaryPlan: rule.sessionBoundaryPlan,
      variableExtractionPlan: rule.variableExtractionPlan,
    });
  }, []);

  const reconcileRuleIds = useCallback(
    (prevRules: HandoffRuleEditorValue[], nextRules: HandoffRuleEditorValue[]) => {
      const previousByClientRuleId = new Map(prevRules.map((rule) => [rule.clientRuleId, rule]));
      const previousByFingerprint = new Map(
        prevRules.map((rule) => [buildRuleFingerprint(rule), rule] as const),
      );
      return nextRules.map((rule) => {
        const previousRule =
          previousByClientRuleId.get(rule.clientRuleId) ??
          previousByFingerprint.get(buildRuleFingerprint(rule));
        if (!previousRule) return rule;
        return { ...rule, id: previousRule.id, clientRuleId: previousRule.clientRuleId };
      });
    },
    [buildRuleFingerprint],
  );

  const applyParsedEditorState = useCallback(
    (nextValue: ReturnType<typeof parseHandoffRulesValue>) => {
      setRules((previousRules) => reconcileRuleIds(previousRules, nextValue.rules));
      setCanvasAgentIds(nextValue.canvasAgentIds);
      setNodePositions(nextValue.nodePositions);
      setJsonError(null);
    },
    [reconcileRuleIds],
  );

  useEffect(() => {
    if (!value) {
      setRules([]);
      setCanvasAgentIds([]);
      setNodePositions({});
      setJsonError(null);
      setSelectedRuleId(null);
      setIsDrawerOpen(false);
      setFocusedSection('route');
      setAssistantSearch('');
      setIsAssistantPickerOpen(false);
      setSaveState('idle');
      migratedInputRef.current = null;
      lastLocalSerializedValueRef.current = null;
      lastSavedSerializedValueRef.current = '[]';
      lastValidSerializedValueRef.current = '[]';
      return;
    }
    const isLocalEcho = lastLocalSerializedValueRef.current === value;
    if (isLocalEcho) {
      lastLocalSerializedValueRef.current = null;
    }
    const parsed = parseHandoffRulesValue(value);
    if (parsed.hasError) {
      setJsonError(invalidJsonMessage);
      setRules([]);
      setCanvasAgentIds([]);
      return;
    }
    const normalizedRules = parsed.rules;
    const normalizedJsonValue = toSerializedDocumentValue(
      normalizedRules,
      parsed.canvasAgentIds,
      parsed.nodePositions,
    );
    applyParsedEditorState(parsed);
    lastValidSerializedValueRef.current = normalizedJsonValue;
    if (!isLocalEcho) {
      lastSavedSerializedValueRef.current = normalizedJsonValue;
      setSaveState('idle');
    }
  }, [applyParsedEditorState, invalidJsonMessage, toSerializedDocumentValue, value]);

  useEffect(() => {
    if (!value) return;
    const parsed = parseHandoffRulesValue(value);
    if (parsed.hasError) return;
    const normalizedJsonValue = toSerializedDocumentValue(
      parsed.rules,
      parsed.canvasAgentIds,
      parsed.nodePositions,
    );
    if (normalizedJsonValue === value) return;
    if (migratedInputRef.current === value) return;
    migratedInputRef.current = value;
    lastLocalSerializedValueRef.current = normalizedJsonValue;
    onChange(normalizedJsonValue);
  }, [onChange, toSerializedDocumentValue, value]);

  const syncSerializedRules = useCallback(
    (
      nextRules: HandoffRuleEditorValue[],
      nextCanvasAgentIds: string[],
      nextNodePositions: Record<string, { x: number; y: number }>,
    ) => {
      const jsonValue = toSerializedDocumentValue(nextRules, nextCanvasAgentIds, nextNodePositions);
      lastLocalSerializedValueRef.current = jsonValue;
      lastValidSerializedValueRef.current = jsonValue;
      onChange(jsonValue);
    },
    [onChange, toSerializedDocumentValue],
  );

  const syncAndMarkDirty = useCallback(
    (
      nextRules: HandoffRuleEditorValue[],
      nextCanvasAgentIds: string[],
      nextNodePositions: Record<string, { x: number; y: number }>,
    ) => {
      setSaveState('dirty');
      syncSerializedRules(nextRules, nextCanvasAgentIds, nextNodePositions);
    },
    [syncSerializedRules],
  );

  const handleJsonChange = useCallback(
    (newValue: string) => {
      onChange(newValue);
      setSaveState('dirty');
      const parsed = parseHandoffRulesValue(newValue);
      if (parsed.hasError) {
        setJsonError(invalidJsonMessage);
        return;
      }
      const normalizedJsonValue = toSerializedDocumentValue(
        parsed.rules,
        parsed.canvasAgentIds,
        parsed.nodePositions,
      );
      lastValidSerializedValueRef.current = normalizedJsonValue;
      applyParsedEditorState(parsed);
    },
    [applyParsedEditorState, invalidJsonMessage, onChange, toSerializedDocumentValue],
  );

  const mutateRules = useCallback(
    (updater: (currentRules: HandoffRuleEditorValue[]) => HandoffRuleEditorValue[]) => {
      setRules((previousRules) => {
        const nextRules = updater(previousRules);
        setJsonError(null);
        syncAndMarkDirty(nextRules, canvasAgentIdsRef.current, nodePositionsRef.current);
        return nextRules;
      });
    },
    [syncAndMarkDirty],
  );

  const updateRule = useCallback(
    (ruleId: string, updater: (rule: HandoffRuleEditorValue) => HandoffRuleEditorValue) => {
      mutateRules((currentRules) =>
        currentRules.map((rule) => (rule.id === ruleId ? updater(rule) : rule)),
      );
    },
    [mutateRules],
  );

  const addRule = useCallback(() => {
    const newRule = createEmptyHandoffRule();
    mutateRules((currentRules) => [...currentRules, newRule]);
    setSelectedRuleId(newRule.id);
    setIsDrawerOpen(true);
  }, [mutateRules]);

  const removeRule = useCallback(
    (ruleId: string) => {
      mutateRules((currentRules) => currentRules.filter((rule) => rule.id !== ruleId));
      setSelectedRuleId((currentSelectedRuleId) =>
        currentSelectedRuleId === ruleId ? null : currentSelectedRuleId,
      );
      setIsDrawerOpen(false);
    },
    [mutateRules],
  );

  const handleSelectAssistant = useCallback(
    (agentId: string) => {
      const currentCanvasAgentIds = canvasAgentIdsRef.current;
      const currentNodePositions = nodePositionsRef.current;
      const nextCanvasAgentIds = currentCanvasAgentIds.includes(agentId)
        ? currentCanvasAgentIds
        : [...currentCanvasAgentIds, agentId];
      const nextNodePositions = {
        ...currentNodePositions,
        [agentId]:
          currentNodePositions[agentId] ?? getNextAssistantSpawnPosition(currentNodePositions),
      };
      canvasAgentIdsRef.current = nextCanvasAgentIds;
      nodePositionsRef.current = nextNodePositions;
      setCanvasAgentIds(nextCanvasAgentIds);
      setNodePositions(nextNodePositions);
      syncAndMarkDirty(rules, nextCanvasAgentIds, nextNodePositions);
      setIsAssistantPickerOpen(false);
    },
    [rules, syncAndMarkDirty],
  );

  const updateNodePosition = useCallback(
    (agentId: string, position: { x: number; y: number }) => {
      const nextNodePositions = { ...nodePositionsRef.current, [agentId]: position };
      setNodePositions(nextNodePositions);
      syncAndMarkDirty(rules, canvasAgentIdsRef.current, nextNodePositions);
    },
    [rules, syncAndMarkDirty],
  );

  const openDrawerForRule = useCallback((ruleId: string, section?: FocusedSection) => {
    setSelectedRuleId(ruleId);
    setIsDrawerOpen(true);
    if (section) setFocusedSection(section);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const issues = validateHandoffRules(rules);
  const hasValidationErrors = issues.length > 0;
  const missingRuleAgentIds = useMemo(() => {
    const agentIdsWithRules = new Set(
      rules.flatMap((rule) => [rule.fromAgentId, rule.toAgentId]).filter(Boolean),
    );
    return canvasAgentIds.filter((agentId) => !agentIdsWithRules.has(agentId));
  }, [canvasAgentIds, rules]);
  const hasAgentsWithoutRules = missingRuleAgentIds.length > 0;

  const revertToLastValidJson = useCallback(() => {
    const fallbackValue = lastValidSerializedValueRef.current;
    lastLocalSerializedValueRef.current = fallbackValue;
    onChange(fallbackValue);
    const parsed = parseHandoffRulesValue(fallbackValue);
    if (parsed.hasError) return;
    applyParsedEditorState(parsed);
    setSaveState('dirty');
  }, [applyParsedEditorState, onChange]);

  const formatJsonValue = useCallback(() => {
    const parsed = parseHandoffRulesValue(value);
    if (parsed.hasError) {
      setJsonError(invalidJsonMessage);
      return;
    }
    const formatted = toSerializedDocumentValue(
      parsed.rules,
      parsed.canvasAgentIds,
      parsed.nodePositions,
    );
    lastLocalSerializedValueRef.current = formatted;
    lastValidSerializedValueRef.current = formatted;
    onChange(formatted);
    applyParsedEditorState(parsed);
    setSaveState('dirty');
  }, [applyParsedEditorState, invalidJsonMessage, onChange, toSerializedDocumentValue, value]);

  const markCurrentAsSaved = useCallback(() => {
    lastSavedSerializedValueRef.current = toSerializedDocumentValue(
      rules,
      canvasAgentIdsRef.current,
      nodePositionsRef.current,
    );
    setSaveState('saved');
  }, [rules, toSerializedDocumentValue]);

  /** Drop canvas agents not referenced by any rule (call before save). */
  const pruneUnusedCanvasAgents = useCallback(() => {
    const pruned = pruneHandoffCanvasToReferencedAgents(rules, nodePositionsRef.current);
    canvasAgentIdsRef.current = pruned.canvasAgentIds;
    nodePositionsRef.current = pruned.nodePositions;
    setCanvasAgentIds(pruned.canvasAgentIds);
    setNodePositions(pruned.nodePositions);
    syncSerializedRules(rules, pruned.canvasAgentIds, pruned.nodePositions);
  }, [rules, syncSerializedRules]);

  /**
   * When API rules load without canvas positions, materialize the deterministic
   * auto-layout into the document (without dirtying) so refresh can persist it.
   */
  const seedAutoLayoutPositionsIfEmpty = useCallback(
    (agents: AgentDto[]) => {
      if (Object.keys(nodePositionsRef.current).length > 0) return;
      if (rules.length === 0 && canvasAgentIdsRef.current.length === 0) return;

      const agentsForGraph =
        agents.length > 0
          ? agents
          : canvasAgentIdsRef.current.map((id) => ({ id, name: id }) as AgentDto);
      const { nodes } = buildHandoffCanvasGraph(rules, agentsForGraph, undefined);
      if (nodes.length === 0) return;

      const nextNodePositions = Object.fromEntries(
        nodes.map((node) => [node.id, node.position] as const),
      );
      const nextCanvasAgentIds = Array.from(
        new Set([...canvasAgentIdsRef.current, ...nodes.map((node) => node.id)]),
      );

      canvasAgentIdsRef.current = nextCanvasAgentIds;
      nodePositionsRef.current = nextNodePositions;
      setCanvasAgentIds(nextCanvasAgentIds);
      setNodePositions(nextNodePositions);

      const jsonValue = toSerializedDocumentValue(rules, nextCanvasAgentIds, nextNodePositions);
      lastLocalSerializedValueRef.current = jsonValue;
      lastValidSerializedValueRef.current = jsonValue;
      lastSavedSerializedValueRef.current = jsonValue;
      onChange(jsonValue);
    },
    [onChange, rules, toSerializedDocumentValue],
  );

  const canRecoverJson = Boolean(jsonError) && value !== lastValidSerializedValueRef.current;

  const getRuleError = (rule: HandoffRuleEditorValue): string | null => {
    const issue = issues.find((item) => item.ruleId === rule.id);
    return issue ? issueMessages[issue.code] : null;
  };

  const serializeCurrentValue = (): HandoffRuleRequestDto[] => {
    return serializeHandoffRules(rules);
  };

  const addConnectedRule = (
    sourceAgentId: string,
    targetAgentId: string,
    getAgentLabel: (agentId: string, fallback: string) => string,
  ) => {
    const newRule: HandoffRuleEditorValue = {
      ...createEmptyHandoffRule(),
      name: `${getAgentLabel(sourceAgentId, 'From')} -> ${getAgentLabel(targetAgentId, 'To')}`,
      fromAgentId: sourceAgentId,
      toAgentId: targetAgentId,
    };
    const nextCanvasAgentIds = Array.from(
      new Set([...canvasAgentIdsRef.current, sourceAgentId, targetAgentId]),
    );
    setCanvasAgentIds(nextCanvasAgentIds);
    setRules((currentRules) => {
      const nextRules = [...currentRules, newRule];
      setJsonError(null);
      syncAndMarkDirty(nextRules, nextCanvasAgentIds, nodePositionsRef.current);
      return nextRules;
    });
    openDrawerForRule(newRule.id, 'route');
  };

  useEffect(() => onDirtyChange?.(isDirty), [isDirty, onDirtyChange]);
  useEffect(
    () => onValidationChange?.(hasValidationErrors),
    [hasValidationErrors, onValidationChange],
  );

  return {
    jsonError,
    setJsonError,
    editorMode,
    setEditorMode,
    rules,
    saveState,
    setSaveState,
    canvasAgentIds,
    setCanvasAgentIds,
    nodePositions,
    setNodePositions,
    updateNodePosition,
    isAssistantPickerOpen,
    setIsAssistantPickerOpen,
    assistantSearch,
    setAssistantSearch,
    selectedRuleId,
    isDrawerOpen,
    setIsDrawerOpen,
    focusedSection,
    setFocusedSection,
    isDirty,
    hasAgentsWithoutRules,
    hasValidationErrors,
    missingRuleAgentIds,
    getRuleError,
    selectedRule: rules.find((rule) => rule.id === selectedRuleId) ?? null,
    canRecoverJson,
    handleJsonChange,
    revertToLastValidJson,
    formatJsonValue,
    updateRule,
    addRule,
    removeRule,
    openDrawerForRule,
    closeDrawer,
    handleSelectAssistant,
    addConnectedRule,
    serializeCurrentValue,
    markCurrentAsSaved,
    pruneUnusedCanvasAgents,
    seedAutoLayoutPositionsIfEmpty,
  };
};
